/**
 * UI Store
 *
 * Zustand store for UI preferences and state
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';

interface UIState {
  // Theme
  theme: Theme;
  resolvedTheme: 'light' | 'dark';

  // Sidebar
  sidebarCollapsed: boolean;
  sidebarOpen: boolean;

  // Preferences
  compactMode: boolean;
  animationsEnabled: boolean;

  // Actions
  setTheme: (theme: Theme) => void;
  setResolvedTheme: (theme: 'light' | 'dark') => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  setCompactMode: (compact: boolean) => void;
  setAnimationsEnabled: (enabled: boolean) => void;
  reset: () => void;
}

const initialState = {
  theme: 'system' as Theme,
  resolvedTheme: 'light' as const,
  sidebarCollapsed: false,
  sidebarOpen: true,
  compactMode: false,
  animationsEnabled: true,
};

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      ...initialState,

      setTheme: (theme) =>
        set({ theme }),

      setResolvedTheme: (resolvedTheme) =>
        set({ resolvedTheme }),

      setSidebarCollapsed: (collapsed) =>
        set({ sidebarCollapsed: collapsed }),

      setSidebarOpen: (open) =>
        set({ sidebarOpen: open }),

      toggleSidebar: () =>
        set((state) => ({ sidebarOpen: !state.sidebarOpen })),

      setCompactMode: (compact) =>
        set({ compactMode: compact }),

      setAnimationsEnabled: (enabled) =>
        set({ animationsEnabled: enabled }),

      reset: () =>
        set(initialState),
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({
        theme: state.theme,
        sidebarCollapsed: state.sidebarCollapsed,
        compactMode: state.compactMode,
        animationsEnabled: state.animationsEnabled,
      }),
    }
  )
);

// Selectors
export const selectTheme = (state: UIState) => state.theme;
export const selectResolvedTheme = (state: UIState) => state.resolvedTheme;
export const selectSidebarCollapsed = (state: UIState) => state.sidebarCollapsed;
export const selectSidebarOpen = (state: UIState) => state.sidebarOpen;
export const selectCompactMode = (state: UIState) => state.compactMode;
export const selectAnimationsEnabled = (state: UIState) => state.animationsEnabled;
